import { hasAnyPermission } from '@platform/auth/permissions';
import { useCurrentFeatureFlags } from '@platform/feature-flags';
import { useDebounce } from '@shared/hooks/useDebounce';
import { Dialog, Icon } from '@shared/ui';
import clsx from 'clsx';
import type { FC, KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  normalizeAdminSearchTerm,
  useSearchAdminQuery,
} from '../../features/admin/api/adminSearchApi';
import { useListProfilesQuery } from '../../store/api/rpcSlice';
import { adminHomeLink, visibleAdminNavGroups } from './adminNavGroups';
import { adminSearchDestinations, searchAdminDestinations } from './adminSearchMatch';
import {
  type AdminSearchRow,
  buildAdminSearchSections,
  flattenSections,
  profileSection,
} from './adminSearchSections';
import { currentSearchShortcutLabel, matchesSearchShortcut } from './adminSearchShortcut';

type AdminSearchPaletteProps = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  permissions: string[];
  roles: string[];
};

const LISTBOX_ID = 'admin-search-results';
const optionId = (index: number) => `admin-search-option-${index}`;

/**
 * The server rejects anything shorter, because a one-character term matches most
 * of the people table. Pages still filter from the first keystroke — they are local.
 */
const MIN_ENTITY_QUERY_LENGTH = 2;

/** Long enough to stop firing mid-word, short enough to still feel immediate. */
const ENTITY_SEARCH_DEBOUNCE_MS = 200;

/**
 * Search palette for the admin UI, opened by the nav's Search button or by the
 * keyboard shortcut this component owns.
 *
 * Results come from two places. **Pages** and **profiles** are matched locally —
 * pages against the permission-filtered nav config, profiles against the short list
 * the profile editor already loads — so both are instant. **People** (coaches and
 * members) come from `/admin/search`: debounced, and permission-filtered again
 * server-side, so a slow response or a 403 costs the palette those rows and nothing
 * else.
 */
const AdminSearchPalette: FC<AdminSearchPaletteProps> = ({
  isOpen,
  onOpen,
  onClose,
  permissions,
  roles,
}) => {
  const navigate = useNavigate();
  const { globallyEnabledFlags: enabledFlags } = useCurrentFeatureFlags();
  const [query, setQuery] = useState('');
  // Keyed rather than positional: profiles resolve from their own async query and
  // can arrive after entity rows are already on screen, and they render *above*
  // entity sections (see buildAdminSearchSections) — so a late profile section
  // prepends and shifts every row below it down one slot. A plain numeric index
  // would keep pointing at whatever landed in that slot, not the row the user
  // actually highlighted.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const focusInput = useCallback(() => inputRef.current?.focus(), []);

  const trimmedQuery = query.trim();
  const debouncedQuery = useDebounce(trimmedQuery, ENTITY_SEARCH_DEBOUNCE_MS);

  // Profiles are gated on their own permission, exactly as the Profile Editor route
  // is, and only fetched while the palette is open — the list is small and cached.
  const canSeeProfiles = hasAnyPermission(permissions, ['admin:profiles'], roles);
  const { data: profileData } = useListProfilesQuery(undefined, {
    skip: !isOpen || !canSeeProfiles,
  });

  const destinations = useMemo(() => {
    const groups = visibleAdminNavGroups({ permissions, roles, enabledFlags });
    const canSeeHome = hasAnyPermission(permissions, [...adminHomeLink.requiredPermissions], roles);
    return adminSearchDestinations(groups, canSeeHome ? adminHomeLink : null);
  }, [enabledFlags, permissions, roles]);

  const pageResults = useMemo(
    () => searchAdminDestinations(destinations, query),
    [destinations, query]
  );

  // Skipped while closed, so the palette costs nothing until it is used, and while
  // the term is too short for the server to answer usefully.
  const shouldSearchEntities = isOpen && debouncedQuery.length >= MIN_ENTITY_QUERY_LENGTH;
  const { data: entityData, isFetching: isFetchingEntities } = useSearchAdminQuery(
    { q: debouncedQuery },
    { skip: !shouldSearchEntities }
  );

  // debouncedQuery lags trimmedQuery by up to the debounce window on every
  // keystroke. Without requiring them to match, a row fetched for an earlier,
  // shorter term stays rendered — and selectable, and Enter-able — for that whole
  // window after the box has moved on to something else.
  const debounceHasCaughtUp = debouncedQuery === trimmedQuery;

  // The response's echoed `query` is compared through the same fold the cache key
  // uses (see normalizeAdminSearchTerm's doc). Retyping "Dana" as "dana" is a
  // cache hit, not a new request, so the response that comes back still says
  // "Dana" — a case-sensitive comparison here would call that stale and throw
  // away results that are in fact exactly what was asked for.
  const entityGroups =
    debounceHasCaughtUp &&
    entityData &&
    normalizeAdminSearchTerm(entityData.query) === normalizeAdminSearchTerm(debouncedQuery)
      ? entityData.groups
      : undefined;

  const profiles = useMemo(
    () => profileSection(profileData?.profiles, trimmedQuery),
    [profileData, trimmedQuery]
  );

  const sections = useMemo(
    () => buildAdminSearchSections(pageResults, entityGroups, trimmedQuery.length > 0, [profiles]),
    [pageResults, entityGroups, trimmedQuery, profiles]
  );
  const rows = useMemo(() => flattenSections(sections), [sections]);

  // Locks the highlight onto a specific row's identity as soon as any row
  // exists, instead of leaving it to fall back to "index 0" on every render.
  // Without this, a highlight that was never explicitly moved (the common
  // case — most opens end in an immediate Enter on the top result) would
  // silently track whichever row is *currently* first, and a late-arriving
  // section (profiles resolve async and render above entity rows — see
  // buildAdminSearchSections) can prepend and swap what "first" means out
  // from under a highlight the user already saw and is about to commit to.
  useEffect(() => {
    if (rows.length === 0) return;
    setSelectedKey((current) =>
      current !== null && rows.some((row) => row.key === current) ? current : rows[0].key
    );
  }, [rows]);

  // Resolved by key every render, so a row that shifts position never leaves
  // the cursor pointing at a different row that happened to land in its old
  // slot. Falls back to index 0 only for the render before the effect above
  // has had a chance to lock in a key (e.g. the very first row ever shown).
  const selected = useMemo(() => {
    if (selectedKey === null) return 0;
    const index = rows.findIndex((row) => row.key === selectedKey);
    return index === -1 ? 0 : index;
  }, [rows, selectedKey]);

  // Opening is always a fresh start; a stale query from last time is never what you want.
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedKey(null);
    }
  }, [isOpen]);

  const shortcutOpensPalette = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if (!matchesSearchShortcut(event)) return;
      event.preventDefault();
      if (isOpen) {
        onClose();
      } else {
        onOpen();
      }
    },
    [isOpen, onClose, onOpen]
  );

  useEffect(() => {
    document.addEventListener('keydown', shortcutOpensPalette);
    return () => document.removeEventListener('keydown', shortcutOpensPalette);
  }, [shortcutOpensPalette]);

  const go = useCallback(
    (route: string) => {
      onClose();
      navigate(route);
    },
    [navigate, onClose]
  );

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (rows.length) setSelectedKey(rows[(selected + 1) % rows.length].key);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (rows.length) setSelectedKey(rows[(selected - 1 + rows.length) % rows.length].key);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const target = rows[selected];
      if (target) go(target.route);
    }
  };

  // Rows are numbered across sections, so the arrow keys cross group boundaries.
  let rowIndex = -1;
  const nextRowIndex = () => {
    rowIndex += 1;
    return rowIndex;
  };

  const renderRow = (row: AdminSearchRow) => {
    const index = nextRowIndex();
    const isSelected = index === selected;
    return (
      <div
        aria-selected={isSelected}
        className={clsx(
          'flex cursor-pointer items-center gap-3 border-0 border-l-2 border-solid px-4 py-2',
          isSelected ? 'border-l-sage-500 bg-sage-100' : 'border-l-transparent hover:bg-cement-100'
        )}
        id={optionId(index)}
        key={row.key}
        onClick={() => go(row.route)}
        onMouseMove={() => setSelectedKey(row.key)}
        role="option"
        tabIndex={-1}
      >
        <span className="flex size-7 flex-none items-center justify-center rounded-lg bg-sage-200 text-sage-600">
          <Icon className="text-[17px] text-sage-600">{row.icon}</Icon>
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm text-cement-500">{row.title}</span>
          <span
            className={clsx(
              'truncate text-[11px] text-cement-300',
              row.subtitleIsRoute && 'font-mono'
            )}
          >
            {row.subtitle}
          </span>
        </span>
      </div>
    );
  };

  // Spans the whole gap the entityGroups guard above can hide results for: the
  // debounce window (debounce hasn't caught up yet) as well as the network round
  // trip once it has (isFetchingEntities). A long enough query with the palette
  // open is the only precondition — "pending" is true well before any request is
  // even sent.
  const isSearchingEntities =
    isOpen &&
    trimmedQuery.length >= MIN_ENTITY_QUERY_LENGTH &&
    (!debounceHasCaughtUp || isFetchingEntities);
  const hasRows = rows.length > 0;

  return (
    <Dialog
      aria-label="Admin search"
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={isOpen}
      PaperProps={{ className: 'rounded-xl overflow-hidden m-4' }}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      // MUI's own FocusTrap moves focus into the dialog container as soon as it
      // opens, which wins the race against a plain `autoFocus` on the input when
      // the trigger was a click (it doesn't when the trigger was a keydown, which
      // is why this bug only showed up on one of the two paths). onEntered fires
      // once MUI's enter transition — and its focus management — has settled, so
      // focusing here always runs last.
      TransitionProps={{ onEntered: focusInput }}
    >
      <div className="flex items-center gap-3 border-0 border-b border-solid border-cement-200 px-4 py-3">
        <Icon className="text-cement-300">search</Icon>
        <input
          aria-activedescendant={hasRows ? optionId(selected) : undefined}
          aria-autocomplete="list"
          aria-controls={LISTBOX_ID}
          aria-label="Search admin"
          autoComplete="off"
          className="flex-1 border-0 bg-transparent font-sans text-base text-cement-500 outline-none"
          onChange={(event) => {
            setQuery(event.target.value);
            setSelectedKey(null);
          }}
          onKeyDown={onInputKeyDown}
          placeholder="Search pages, people, profiles…"
          ref={inputRef}
          spellCheck={false}
          type="text"
          value={query}
        />
        <span className="rounded border border-solid border-cement-200 px-1.5 py-px font-mono text-[10px] text-cement-300">
          esc
        </span>
      </div>

      {hasRows ? (
        // Focus stays in the text input and the active row is pointed at with
        // aria-activedescendant, which is why the options carry tabIndex={-1}
        // rather than being tab stops of their own.
        <div
          aria-label="Admin search results"
          className="max-h-80 overflow-y-auto"
          id={LISTBOX_ID}
          role="listbox"
          tabIndex={-1}
        >
          {sections.map((section) => (
            <div key={section.key}>
              <div className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.09em] text-cement-300">
                {section.label}
              </div>
              {section.rows.map(renderRow)}
            </div>
          ))}
        </div>
      ) : (
        <p className="m-0 px-4 py-8 text-center text-sm text-cement-300">
          {isSearchingEntities ? 'Searching…' : `Nothing matches “${trimmedQuery}”.`}
        </p>
      )}

      <div className="flex items-center gap-4 border-0 border-t border-solid border-cement-200 bg-cement-100 px-4 py-2 text-[11px] text-cement-400">
        <span>↑↓ move</span>
        <span>↵ open</span>
        {isSearchingEntities && hasRows && <span className="text-cement-300">searching…</span>}
        <span className="ml-auto">{currentSearchShortcutLabel()} to toggle</span>
      </div>
    </Dialog>
  );
};

export default AdminSearchPalette;
